import React, { Fragment, useEffect, useState } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import Decimal from 'decimal.js';
import Constants, {
  DEFAULT_CUR_ROUND_OFF, QUANTITY, INFINITE_EXPIRY_DATE, QUANTITYROUND,
} from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import {
  DatePicker, Upload, notification, Checkbox, Tabs, Badge, Alert,
} from 'antd';
import { PlusCircleFilled, PlusOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import ManualProductionEntryActions from '@Actions/production/manualProductionEntryActions';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import BOMActions from '@Actions/bomActions';
import CFV2Actions from '@Actions/configurations/cfV2Actions';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import inventoryLocationActions from '@Actions/settings/inventoryLocationActions';
import Helpers from '@Apis/helpers';
import SelectDepartment from '@Components/Common/SelectDepartment';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import RawMaterial from './Tables/RawMaterial';
import ByProduct from './Tables/ByProduct';
import OtherCharge from './Tables/OtherCharge';
import FinishedGood from './Tables/FinishedGood';
import './style.scss';
import FormHelpers from '@Helpers/FormHelpers';
import BatchHelpers from '@Helpers/BatchHelpers';
import RestrictedAccessMessage from '../../../Common/RestrictedAccess/RestrictedAccessMessage';
import DocumentNumberSeqInput from '../../../Admin/Common/DocumentNumberSeqInput';

const { TabPane } = Tabs;

const uploadButton = (
  <div>
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </div>
);

const ManualProductionEntryForm = ({
  user, org, getDocCFV2, getBOMById, getBOMByIdLoading, getMOByIdLoading, createManualProductionEntryLoading, updateMOLoading, getBOMByIdSuccess, location, createManualProductionEntry, getInventoryLocations, inventoryLocations, priceMasking, cfV2DocManufacturingOrder,
}) => {
  const [currentTab, setCurrentTab] = useState('/finished-goods');
  const [selectedTenant, setSelectedTenant] = useState(user?.tenant_info?.tenant_id);
  const [tenantDepartmentId, setTenantDepartmentId] = useState(user?.tenant_info?.default_department_for_production);
  const [fgTenantDepartmentId, setFgTenantDepartmentId] = useState(user?.tenant_info?.default_department_for_fg);
  const [rmTenantDepartmentId, setRmTenantDepartmentId] = useState(user?.tenant_info?.default_department_for_rm);
  const [bpTenantDepartmentId, setBpTenantDepartmentId] = useState(user?.tenant_info?.default_department_for_bp);
  const [scheduledDate, setScheduledDate] = useState(dayjs());
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [finishedGoods, setFinishedGoods] = useState([{
    key: uuidv4(), asset1: '', product_name: '', quantity: '', unitPrice: '', lot: 0, taxId: '', discount: 0,
  }]);
  const [bomLines, setBomLines] = useState({});
  const [bomByProducts, setBomByProducts] = useState({});
  const [extraCharges, setExtraCharges] = useState({});
  const [remarks, setRemarks] = useState('');
  const [trackWastage, setTrackWastage] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [actionType, setActionType] = useState('');
  const [isFgBatchValid, setIsFgBatchValid] = useState(false);
  const [isBpBatchValid, setIsBpBatchValid] = useState(false);
  const history = useHistory();
  const [allStock, updateAllStock] = useState(true);
  const [meNumber, setMeNumber] = useState('');
  const [docSeqId, setDocSeqId] = useState(null);
  const [initialMeNumber, setInitialMeNumber] = useState('');

  const updateAllStockValue = (value, fgTenantProductId, key) => {
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    if (bomLinesCopy?.[fgTenantProductId]?.length) {
      bomLinesCopy[fgTenantProductId] = bomLinesCopy[fgTenantProductId].map((item) => {
        if(item?.key !== key) return item;
        return {
          ...item,
          product_batches: getBatches(item?.estimatedUsage, item?.tenant_product_info?.product_batches, false, value),
          good_stock_only: value,
        };
      });
      setBomLines(bomLinesCopy);  
    }
  };
  useEffect(() => {
    const payload = {
      orgId: user?.tenant_info?.org_id,
      entityName: 'MANUFACTURING_ORDER,BATCH',
    };
    getDocCFV2(payload);
    return () => {
      getBOMByIdSuccess(null);
    };
  }, []);

  const getReadyToProduce = (fgTpId) => {
    const rawMaterials = bomLines[fgTpId];
    return QUANTITY(
      Math.min(
        ...rawMaterials.map((m) => Number(m.currentStock || 0) / (Number(m.quantityPerUnit || 0) * (1 + Number(m.wastage_percentage || 0) / 100))),
      ) || 0, finishedGoods[0]?.tenant_product_info?.uom_info?.precision,
    );
  };

  const isDataValid = () => {
    const batchConfig = org?.organisation?.[0]?.inventory_config?.settings?.batch_management;
    const rawMaterials = bomLines[finishedGoods?.[0]?.tenant_product_info?.tenant_product_id];
    const byProducts = bomByProducts[finishedGoods?.[0]?.tenant_product_info?.tenant_product_id];
    let isValid = true;
    let rmErrorCount = 0;
    for (let i = 0; i < rawMaterials?.length; i++) {
      const consumedQty = QUANTITYROUND(
        Number(Helpers.getValueTotalInObject(
          rawMaterials?.[i]?.product_batches?.filter((batch) => batch?.batch_in_use),
          'consumed_qty',
        ) || 0),
        rawMaterials?.[i]?.tenant_product_info?.uom_info?.precision,
      );

      const estimatedUsage = Number(rawMaterials?.[i]?.estimatedUsage || 0);
      const wastageQuantity = Number(rawMaterials?.[i]?.wastage_quantity || 0);
      const totalUsage = QUANTITYROUND(
        estimatedUsage + wastageQuantity,
        rawMaterials?.[i]?.tenant_product_info?.uom_info?.precision,
      );

      if ((consumedQty < totalUsage) || (estimatedUsage <= 0)) {
        rmErrorCount++;
      }
    }
    const bpBatchValid = byProducts?.length ? BatchHelpers.validateBatchDetailsSimple(batchConfig, byProducts, true) : true;
    const fgQuantityValid = finishedGoods[0]?.quantity > 0;
    const fgCustomFieldValid = finishedGoods?.[0]?.selectedBatch?.custom_fields?.reduce((res,field) => {
      if ((field?.isActive && field?.isRequired) && (field?.fieldValue === undefined || field?.fieldValue === '' || field?.fieldValue === 0)) {
        return false;
      }
      return res;
    }, true);
    const isBpQuantityValid = !(byProducts?.filter((item) => !item?.estimatedUsage)?.length > 0);
    if (rmErrorCount > 0 || !isFgBatchValid || !bpBatchValid || !fgQuantityValid || !isBpQuantityValid || !fgCustomFieldValid) {
      isValid = false;
    }
    return {
      isValid,
      rmErrorCount,
      isFgBatchValid,
      isBpBatchValid: bpBatchValid,
      isFgQuantityValid: fgQuantityValid,
      isBpQuantityValid,
    };
  };

  const inputArray = [
    {
      type: 'docSequence',
      title: 'Manual Entry#',
      value: meNumber,
    },
    {
      type: 'tenant',
      title: 'Location',
      value: selectedTenant,
    },
    {
      type: 'department',
      departmentType: 'PRODUCTION',
      title: 'Production Department',
      value: tenantDepartmentId,
    },
    {
      type: 'date',
      title: 'Entry Date',
      value: scheduledDate,
    },
    {
      type: 'remarks',
      title: 'Remarks',
      value: remarks,
    },
    {
      type: 'button',
      title: 'Advanced Settings',
      value: showAdvancedSettings,
    },
    {
      type: 'department',
      departmentType: 'FG',
      title: 'Finished Goods Department',
      value: fgTenantDepartmentId,
    },
    {
      type: 'department',
      departmentType: 'RM',
      title: 'Raw Materials Department',
      value: rmTenantDepartmentId,
    },
    {
      type: 'department',
      departmentType: 'BP',
      title: 'By Products Department',
      value: bpTenantDepartmentId,
    },
    {
      type: 'checkbox',
      title: 'Track Wastage of Raw Materials',
      value: trackWastage,
    },
  ];

  const handleTenantChange = (value) => {
    const departments = user?.user_tenants?.find((item) => item?.tenant_id === value);

    setSelectedTenant(value);
    setTenantDepartmentId(departments?.default_department_for_production);
    setFgTenantDepartmentId(departments?.default_department_for_fg);
    setRmTenantDepartmentId(departments?.default_department_for_rm);
    setBpTenantDepartmentId(departments?.default_department_for_bp);
    setCurrentTab('/finished-goods');
    setFinishedGoods([{
      key: uuidv4(), asset1: '', product_name: '', quantity: '', unitPrice: '', lot: 0, taxId: '', discount: 0,
    }]);
    setBomLines({});
    setBomByProducts({});
    setExtraCharges({});

    getInventoryLocations(departments?.default_department_for_fg, '', null, true);
  };

  const handleDepartmentChange = (value, type) => {
    if (type === 'PRODUCTION') {
      setTenantDepartmentId(value);
    }
    if (type === 'FG') {
      setFgTenantDepartmentId(value);
      getInventoryLocations(value, '', null, true);
    }
    if (type === 'RM') {
      setRmTenantDepartmentId(value);
    }
    if (type === 'BP') {
      setBpTenantDepartmentId(value);
    }
    setCurrentTab('/finished-goods');
    setFinishedGoods([{
      key: uuidv4(), asset1: '', product_name: '', quantity: '', unitPrice: '', lot: 0, taxId: '', discount: 0,
    }]);
    setBomLines({});
    setBomByProducts({});
    setExtraCharges({});
  };

  function getFgCosting(fgTenantProductId, fgQty) {
    const rawMaterials = bomLines[fgTenantProductId];
    const charges = extraCharges[fgTenantProductId]?.filter((item) => item?.charge_amount > 0);
    const rm = rawMaterials?.filter((item) => item?.fg_tenant_product_id == fgTenantProductId);
    const chg = charges?.filter((item) => item?.fg_tenant_product_id == fgTenantProductId);
    let totalRmCost = 0;
    let totalCharges = 0;
    for (let i = 0; i < rm?.length; i++) {
      const record = rm[i];
      totalRmCost += (record?.costPerUnit * (record.estimatedUsage + (trackWastage ? Number(record?.wastage_quantity) : 0)));
    }
    for (let i = 0; i < chg?.length; i++) {
      const record = chg[i];
      totalCharges += Number(record?.charge_amount);
    }
    return {
      rmCostPerUnit: (totalRmCost / fgQty) || 0,
      chargePerUnit: (totalCharges / fgQty) || 0,
      totalCostPerUnit: ((totalRmCost / fgQty) + (totalCharges / fgQty)) || 0,
    };
  }

  const getBatches = (selectedQty, productBatches, noBatchReset, allStock) => {
    let remainingQty = new Decimal(selectedQty);
    const batches = JSON.parse(JSON.stringify(productBatches || []));
  
    for (let i = 0; i < batches?.length; i++) {
      batches[i].consumed_qty = 0;
      batches[i].batch_in_use = noBatchReset ? batches[i].batch_in_use : true;
    }
  
    for (let i = 0; i < batches?.length; i++) {

      if (allStock && batches[i]?.is_rejected_batch) {
        continue;
      }

      if (batches[i]?.batch_in_use) {
        const availableQty = new Decimal(batches[i]?.available_qty || 0);
        const consumedQty = new Decimal(batches[i]?.consumed_qty || 0);
  
        if (remainingQty.gt(0) && consumedQty.lt(availableQty)) {
          const qtyToConsume = Decimal.min(availableQty, remainingQty);
          batches[i].consumed_qty = qtyToConsume.toNumber();
          remainingQty = remainingQty.minus(qtyToConsume);
        } else {
          batches[i].consumed_qty = 0;
          remainingQty = remainingQty.minus(new Decimal(batches[i].consumed_qty));
        }
      }
    }
    return batches;
  };

  const getBomLinesWithKeys = (bom, key) => {
    const rawMaterialLines = [];
    const getBomRawMaterials = (bomLines, parentQuantity, parentKey) => {
      for (let i = 0; i < bomLines?.length; i++) {
        const item = bomLines[i];
        const currentParentkey = uuidv4();
        const parentQuantityPerUnit = (((item.bom_line_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / parentQuantity;
        const estimatedUsage = ((((item.bom_line_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / (item.bom_quantity || 0)) * item.bom_quantity;
        const wastageQuantity = (((((item.bom_line_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / (item.bom_quantity || 0)) * item.bom_quantity) * (item?.wastage_percentage / 100);
        rawMaterialLines.push({
          ...item,
          key: currentParentkey,
          parentKey,
          productSkuName: item?.product_info?.product_sku_name,
          product_category_info: item?.product_info?.product_category_info,
          bomLineId: item?.bom_line_id,
          tenantProductId: item?.tenant_product_info?.tenant_product_id,
          manufacturingDateFormat: item?.product_info?.manufacturing_date_format,
          expiryDateFormat: item?.product_info?.expiry_date_format,
          tenant_product_info: {
            ...item?.tenant_product_info,
            internal_sku_code: item?.product_info?.internal_sku_code,
            product_batches: item.tenant_product_info.product_batches?.map((i) => ({
              ...i, batch_in_use: true, available_qty: (i?.available_qty / item?.uom_info?.ratio) * item.tenant_product_info?.uom_info?.ratio, fg_tenant_product_id: bom?.tenant_product_info?.tenant_product_id,
            })),
          },
          currentStock: item?.tenant_product_info?.on_hand_qty,
          costPerUnit: item?.tenant_product_info?.cost_price,
          quantityPerUnit: (((item.bom_line_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / item.bom_quantity,
          parentQuantityPerUnit,
          originalEstimatedUsage: QUANTITY(estimatedUsage, item?.tenant_product_info?.uom_info?.precision),
          estimatedUsage: QUANTITY(estimatedUsage, item?.tenant_product_info?.uom_info?.precision),
          wastage_quantity: QUANTITY(wastageQuantity, item?.tenant_product_info?.uom_info?.precision),
          uomId: item?.tenant_product_info?.uom_info?.uom_id,
          uomInfo: item?.tenant_product_info?.uom_info,
          uom_info: item?.tenant_product_info?.uom_info,
          fgQuantity: bom?.quantity,
          bom_id: bom?.bom_id,
          fg_tenant_product_id: bom?.tenant_product_info?.tenant_product_id,
          child_bom_id: item?.child_bom_id,
          parent_bom_id: item?.parent_bom_id,
          product_batches: Helpers.batchSelectionMethod(Helpers.getBatchMethod(item.tenant_product_info), getBatches(trackWastage ? (estimatedUsage + wastageQuantity) : estimatedUsage, item?.tenant_product_info?.product_batches, false, allStock), trackWastage ? (estimatedUsage + wastageQuantity) : estimatedUsage, allStock),
          batchConsumptionMethod: Helpers.getBatchMethod(item?.tenant_product_info),
          default_outwards_method: item?.product_info?.default_outwards_method,
          good_stock_only: allStock,
        });
        getBomRawMaterials(item?.child_bom_lines, estimatedUsage, currentParentkey);
      }
      return rawMaterialLines;
    };
    return getBomRawMaterials(bom?.bom_lines_last_level_view, bom?.bom_quantity, key);
  };

  // Select FG From BOM Selector
  function onSelectBom(key, value) {
    let fgItemId = '';
    getBOMById(user?.tenant_info?.org_id, selectedTenant, value?.bom_id, rmTenantDepartmentId, 'last_level', (bom) => {
      fgItemId = bom?.tenant_product_info?.tenant_product_id;
      const finishedGoodsCopy = JSON.parse(JSON.stringify(finishedGoods));
      const batchCustomFields = CustomFieldHelpers.getCfStructure(cfV2DocManufacturingOrder?.data?.batch_custom_fields, true);
      for (let i = 0; i < finishedGoods?.length; i++) {
        if (finishedGoods[i]?.key === key) {
          const newBatch = {
            tenant_product_id: bom?.tenant_product_info?.tenant_product_id,
            expiry_date: bom?.product_info?.expiry_days > 0 ? (dayjs().endOf('day').add(bom?.product_info?.expiry_days, 'day')) : dayjs(INFINITE_EXPIRY_DATE, 'YYYY-MM-DD').endOf('day'),
            lot_number: '',
            custom_batch_number: `${bom?.product_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${bom?.product_info?.product_batch_counter}`,
            cost_price: bom?.offer_price || 0,
            mrp: bom?.product_info?.selling_price || 0,
            selling_price: 0,
            uom_id: bom?.uom_id,
            manual_entry: false,
            inventory_location_id: inventoryLocations?.inventory_location?.filter((i) => i?.is_active)?.[0]?.inventory_location_id,
            inventory_location_path: inventoryLocations?.inventory_location?.filter((i) => i?.is_active)?.[0]?.inventory_location_path,
            is_rejected_batch: false,
            custom_fields: batchCustomFields,
          };

          const fgItem = JSON.parse(JSON.stringify(finishedGoods[i]));
          fgItem.bom_id = bom?.bom_id;
          fgItem.tenant_product_info = {
            ...bom?.tenant_product_info,
            internal_sku_code: bom?.internal_sku_code,
          };
          fgItem.quantity = bom?.bom_quantity;
          fgItem.flexible_consumption = bom?.flexible_consumption;
          fgItem.bom_info = bom;
          fgItem.bom_unit_rm_cost = bom?.item_unit_cost_with_wastage;
          fgItem.bom_unit_charges = bom?.bom_extra_charges_per_unit;
          fgItem.bom_unit_total = Number(bom?.item_unit_cost_with_wastage) + Number(bom?.bom_extra_charges_per_unit);
          fgItem.bom_cost = bom?.bom_cost;
          fgItem.selectedBatch = newBatch;
          fgItem.showMoreBatchInfo = false;
          fgItem.remarks = bom?.product_info?.description?.replace(/<[^>]+>/g, '');
          fgItem.manufacturingDateFormat = bom?.product_info?.manufacturing_date_format;
          fgItem.expiryDateFormat = bom?.product_info?.expiry_date_format;
          fgItem.expiryDays = bom?.product_info?.expiry_days;
          finishedGoodsCopy[i] = fgItem;

          setBomLines({
            ...bomLines,
            [bom?.tenant_product_info?.tenant_product_id]: getBomLinesWithKeys(bom, null),
          });
          setBomByProducts({
            ...bomByProducts,
            [bom?.tenant_product_info?.tenant_product_id]: bom.bom_by_products?.map((item) => ({
              ...item,
              selectedBatch: {
                tenant_product_id: item?.tenant_product_info?.tenant_product_id,
                expiry_date: item?.product_info?.expiry_days > 0 ? (dayjs().endOf('day').add(item?.product_info?.expiry_days, 'day')) : dayjs(INFINITE_EXPIRY_DATE, 'YYYY-MM-DD').endOf('day'),
                lot_number: '',
                custom_batch_number: `${item?.product_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${item?.product_info?.product_batch_counter}`,
                cost_price: 0,
                mrp: 0,
                selling_price: 0,
                uom_id: item?.uom_id,
                manual_entry: false,
                inventory_location_id: null,
                inventory_location_path: null,
                is_rejected_batch: false,
                custom_fields: batchCustomFields,
              },
              quantityPerUnit: (((item.bom_bp_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / (item.bom_quantity || 0),
              estimatedUsage: ((((item.bom_bp_quantity * item?.uom_info?.ratio) / item?.tenant_product_info?.uom_info?.ratio) || 0) / (item.bom_quantity || 0)) * item?.bom_quantity,
              tenantProductId: item?.tenant_product_info?.tenant_product_id,
              tenant_product_info: {
                ...item?.tenant_product_info,
                internal_sku_code: item?.product_info?.internal_sku_code,
              },
              bomByProductId: item?.bom_by_product_id,
              asset1: item?.product_info?.assets?.[0]?.url,
              isPerishable: item?.product_info?.is_perishable,
              productSkuName: item?.product_info?.product_sku_name,
              product_category_info: item?.product_info?.product_category_info,
              uomId: item?.tenant_product_info?.uom_info?.uom_id,
              uomInfo: item?.tenant_product_info?.uom_info,
              uom_info: item?.tenant_product_info?.uom_info,
              fgQuantity: item?.bom_quantity,
              bom_id: bom?.bom_id,
              fg_tenant_product_id: bom?.tenant_product_info?.tenant_product_id,
              manufacturingDateFormat: item?.product_info?.manufacturing_date_format,
              expiryDateFormat: item?.product_info?.expiry_date_format,
              key: uuidv4(),
              parentKey: null,
            })),
          });

          setExtraCharges({
            ...extraCharges,
            [bom?.tenant_product_info?.tenant_product_id]: bom?.extra_charges?.map((item) => ({
              ...item,
              costPerUnit: (item.charge_amount / bom?.bom_quantity)?.toFixed(DEFAULT_CUR_ROUND_OFF),
              key: uuidv4(),
              fgQuantity: bom?.bom_quantity,
              bom_id: bom?.bom_id,
              fg_tenant_product_id: bom?.tenant_product_info?.tenant_product_id,
            })),
          });

          setFinishedGoods(finishedGoodsCopy);
        }
      }
    });
  }

  // Delete FG Line
  const handleDeleteFinishedGood = (key) => {
    const removedFg = finishedGoods.find((item) => item.key === key);
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const bomByProductsCopy = JSON.parse(JSON.stringify(bomByProducts));
    const extraChargesCopy = JSON.parse(JSON.stringify(extraCharges));

    delete bomLinesCopy[removedFg?.tenant_product_info?.tenant_product_id];
    delete bomByProductsCopy[removedFg?.tenant_product_info?.tenant_product_id];
    delete extraChargesCopy[removedFg?.tenant_product_info?.tenant_product_id];

    const copyData = finishedGoods.filter((item) => item.key !== key);

    setFinishedGoods(copyData);
    setBomLines(bomLinesCopy);
    setBomByProducts(bomByProductsCopy);
    setExtraCharges(extraChargesCopy);

    if (finishedGoods?.length === 1) {
      setFinishedGoods([{
        key: uuidv4(), asset1: '', product_name: '', quantity: '', unitPrice: '', lot: 0, taxId: '', discount: 0,
      }]);
    }
  };

  // Change FG Quantity
  const handleFgQuantityChange = (tenantProductId, value, key) => {
    // update FG line quantity
    const finishedGoodsCopy = JSON.parse(JSON.stringify(finishedGoods));
    for (let i = 0; i < finishedGoods?.length; i++) {
      if (finishedGoods[i]?.key === key) {
        const fgItem = JSON.parse(JSON.stringify(finishedGoods[i]));
        fgItem.quantity = value;
        finishedGoodsCopy[i] = fgItem;
        break;
      }
    }

    // Update RM line quantity
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const productBomLines = bomLinesCopy[tenantProductId];
    const updateRmQuantity = (currentKey, currentValue) => {
      for (let i = 0; i < productBomLines?.length; i++) {
        if (productBomLines[i]?.key === currentKey) {
          productBomLines[i].originalEstimatedUsage = QUANTITY(Number(currentValue), productBomLines[i]?.uom_info?.precision);
          productBomLines[i].estimatedUsage = QUANTITY(Number(currentValue), productBomLines[i]?.uom_info?.precision);
          const wastageQty = (Number(currentValue) || 0) * (Number(productBomLines[i]?.wastage_percentage) / 100);
          productBomLines[i].wastage_quantity = QUANTITY(wastageQty, productBomLines[i]?.uom_info?.precision);
          productBomLines[i].fgQuantity = value;
          productBomLines[i].product_batches = getBatches(trackWastage ? (productBomLines[i].estimatedUsage + productBomLines[i].wastage_quantity) : productBomLines[i].estimatedUsage, productBomLines[i]?.product_batches, false, productBomLines[i]?.good_stock_only);
          for (let j = 0; j < productBomLines?.length; j++) {
            if (productBomLines[j]?.parentKey === currentKey) {
              updateRmQuantity(productBomLines[j]?.key, Number(productBomLines[j]?.parentQuantityPerUnit) * (Number(currentValue) + Number(wastageQty)));
            }
          }
        }
      }
    };
    for (let i = 0; i < productBomLines?.length; i++) {
      if (productBomLines[i]?.parentKey === null) {
        updateRmQuantity(productBomLines[i]?.key, (Number(value) * productBomLines[i]?.quantityPerUnit) || 0);
      }
    }
    bomLinesCopy[tenantProductId] = productBomLines;

    // Update By Products line quantity
    const bomByProductsCopy = JSON.parse(JSON.stringify(bomByProducts));
    const productBomByProducts = bomByProductsCopy[tenantProductId];
    for (let i = 0; i < productBomByProducts?.length; i++) {
      productBomByProducts[i].estimatedUsage = ((value || 0) * productBomByProducts[i]?.quantityPerUnit);
      productBomByProducts[i].fgQuantity = value;
    }
    bomByProductsCopy[tenantProductId] = productBomByProducts;

    // Update Extra Charges line quantity
    const extraChargesCopy = JSON.parse(JSON.stringify(extraCharges));
    const productExtraCharges = extraChargesCopy[tenantProductId];
    for (let i = 0; i < productExtraCharges?.length; i++) {
      productExtraCharges[i].charge_amount = value * parseFloat(productExtraCharges[i].costPerUnit);
      productExtraCharges[i].fgQuantity = value;
    }
    extraChargesCopy[tenantProductId] = productExtraCharges;

    setFinishedGoods(finishedGoodsCopy);
    setBomLines(bomLinesCopy);
    setBomByProducts(bomByProductsCopy);
    setExtraCharges(extraChargesCopy);
  };

  // Change RM Quantity
  const handleRmQuantityChange = (fgTenantProductId, value, key, wastage) => {
    // Update RM line estimated quantity
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const productBomLines = bomLinesCopy[fgTenantProductId];
    const updateRmQuantity = (currentKey, currentValue, wastageValue) => {
      for (let i = 0; i < productBomLines?.length; i++) {
        if (productBomLines[i]?.key === currentKey) {
          productBomLines[i].wastage_quantity = Number(wastageValue) || '';
          productBomLines[i].estimatedUsage = Number(currentValue) || '';
          productBomLines[i].product_batches = getBatches(trackWastage ? (Number(currentValue) + Number(wastageValue)) : Number(currentValue), productBomLines[i]?.product_batches, true, productBomLines[i]?.good_stock_only);
          const wastageQty = productBomLines[i].wastage_quantity;
          for (let j = 0; j < productBomLines?.length; j++) {
            if (productBomLines[j]?.parentKey === currentKey) {
              updateRmQuantity(productBomLines[j]?.key, Number(productBomLines[j]?.parentQuantityPerUnit) * (Number(currentValue) + Number(wastageQty)));
            }
          }
        }
      }
    };
    updateRmQuantity(key, value, wastage);
    bomLinesCopy[fgTenantProductId] = productBomLines;
    setBomLines(bomLinesCopy);
  };

  const updateRmBatchConsumptionMethod = (fgTenantProductId, value, key) => {
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const productBomLines = bomLinesCopy[fgTenantProductId];
    for (let i = 0; i < productBomLines?.length; i++) {
      if (productBomLines[i]?.key === key) {
        productBomLines[i].batchConsumptionMethod = value;
        productBomLines[i].product_batches = Helpers.batchSelectionMethod(value, productBomLines[i].product_batches, productBomLines[i].estimatedUsage, productBomLines[i]?.good_stock_only, null, productBomLines[i]?.uom_info);
      }
    }
    bomLinesCopy[fgTenantProductId] = productBomLines;
    setBomLines(bomLinesCopy);
  }

  // Change BP Quantity
  const handleBpQuantityChange = (fgTenantProductId, value, key) => {
    // Update BP line estimated quantity
    const bomByProductsCopy = JSON.parse(JSON.stringify(bomByProducts));
    const byProductsLines = bomByProductsCopy[fgTenantProductId];
    for (let i = 0; i < byProductsLines?.length; i++) {
      if (byProductsLines[i]?.key === key) {
        byProductsLines[i].estimatedUsage = value || '';
      }
    }
    bomByProductsCopy[fgTenantProductId] = byProductsLines;

    setBomByProducts(bomByProductsCopy);
  };

  // Change FG Description
  const handleChangeRemarks = (tenantProductId, value, key) => {
    const finishedGoodsCopy = JSON.parse(JSON.stringify(finishedGoods));
    for (let i = 0; i < finishedGoods?.length; i++) {
      if (finishedGoods[i]?.key === key) {
        const fgItem = JSON.parse(JSON.stringify(finishedGoods[i]));
        fgItem.bom_info.product_info.description = value;
        fgItem.remarks = value;
        finishedGoodsCopy[i] = fgItem;
        break;
      }
    }

    setFinishedGoods(finishedGoodsCopy);
  };

  // Exclude FG Product
  function getExcludedProduct() {
    const excludedProducts = [];
    const fgTenantProductId = finishedGoods?.[0]?.tenant_product_info?.tenant_product_id;
    for (let i = 0; i < finishedGoods?.length; i++) {
      if (finishedGoods[i]?.tenant_product_info?.tenant_product_id) excludedProducts.push(finishedGoods[i]?.tenant_product_info?.tenant_product_id?.toString());
    }
    for (let i = 0; i < bomByProducts[fgTenantProductId]?.length; i++) {
      if (bomByProducts[fgTenantProductId]?.[i]?.tenant_product_info?.tenant_product_id) excludedProducts.push(bomByProducts[fgTenantProductId]?.[i]?.tenant_product_info?.tenant_product_id?.toString());
    }
    for (let i = 0; i < bomLines[fgTenantProductId]?.length; i++) {
      if (bomLines[fgTenantProductId]?.[i]?.tenant_product_info?.tenant_product_id) excludedProducts.push(bomLines[fgTenantProductId]?.[i]?.tenant_product_info?.tenant_product_id?.toString());
    }
    return excludedProducts;
  }

  function getMoComponents(lines) {
    const skuLines = {};
    const tenantSkuIdList = Object.keys(lines);
    for (let i = 0; i < tenantSkuIdList?.length; i++) {
      const tenantSkuId = tenantSkuIdList[i];
      if (lines[tenantSkuId]?.length) {
        const allSkuLines = Helpers.transformArrayToTree([...lines[tenantSkuId]]);
        for (let j = 0; j < allSkuLines?.length; j++) {
          if (skuLines.hasOwnProperty(allSkuLines[j]?.tenant_product_info?.tenant_product_id)) {
            if (finishedGoods?.length !== 1) {
              skuLines[allSkuLines[j]?.tenant_product_info?.tenant_product_id].estimatedUsage += (allSkuLines[j]?.quantityPerUnit * allSkuLines[j]?.fgQuantity);
              skuLines[allSkuLines[j]?.tenant_product_info?.tenant_product_id].wastage_quantity = (Number(skuLines[allSkuLines[j]?.tenant_product_info?.tenant_product_id].wastage_quantity) + (Number(allSkuLines[j]?.wastage_quantity)));
            }
          } else if (finishedGoods?.length !== 1) {
            skuLines[allSkuLines[j]?.tenant_product_info?.tenant_product_id] = {
              ...allSkuLines[j],
              estimatedUsage: (allSkuLines[j]?.quantityPerUnit || 0) * (allSkuLines[j]?.fgQuantity || 0),
            };
          } else {
            skuLines[allSkuLines[j]?.tenant_product_info?.tenant_product_id] = {
              ...allSkuLines[j],
            };
          }
        }
      }
    }
    return Object.values(skuLines);
  }

  // Add New RM
  function addNewRowRawMaterial() {
    const fgTenantProductId = finishedGoods?.[0]?.tenant_product_info?.tenant_product_id;
    const copyData = Object.values(bomLines).flat();

    copyData.push({
      key: uuidv4(),
      parentKey: null,
      fg_tenant_product_id: fgTenantProductId,
      bom_id: finishedGoods?.[0]?.bom_id,
      parent_bom_id: finishedGoods?.[0]?.bom_id,
      batchConsumptionMethod: Helpers.getBatchMethod(bomLines?.tenant_product_info),
    });

    setBomLines({
      ...bomLines,
      [fgTenantProductId]: copyData,
    });
  }

  // Add New BP
  function addNewRowByProduct() {
    const fgTenantProductId = finishedGoods?.[0]?.tenant_product_info?.tenant_product_id;
    const copyData = bomByProducts[fgTenantProductId]?.length ? JSON.parse(JSON.stringify(bomByProducts[fgTenantProductId])) : [];
    copyData.push({
      key: uuidv4(),
      fg_tenant_product_id: fgTenantProductId,
      parentKey: null,
    });

    setBomByProducts({
      ...bomByProducts,
      [fgTenantProductId]: copyData,
    });
  }

  const fgLinesMapping = (data) => {
    const bomFgTenantProductIdMapping = {};
    for (let i = 0; i < data?.length; i++) {
      if (bomFgTenantProductIdMapping.hasOwnProperty(data[i]?.fg_tenant_product_id)) {
        bomFgTenantProductIdMapping[data[i]?.fg_tenant_product_id].push({
          ...data[i],
        });
      } else {
        bomFgTenantProductIdMapping[data[i]?.fg_tenant_product_id] = [{
          ...data[i],
        }];
      }
    }
    return bomFgTenantProductIdMapping;
  };
  // Delete RM Line
  const handleDeleteRawMaterial = (productSkuId, rmKey) => {
    const copyData = JSON.parse(JSON.stringify(bomLines));
    const flatData = Object.values(copyData).flat();
    const uniqueRmSkuIds = [...new Set(flatData.map((item) => item.product_sku_id))];
    if (finishedGoods?.length === 1) {
      if (flatData?.length > 1) {
        const copyBomLineArray = flatData.filter((item) => item.key !== rmKey);
        const copyData1 = fgLinesMapping(copyBomLineArray);
        setBomLines(copyData1);
      } else {
        notification.open({
          type: 'error',
          message: 'Atleast one raw material is required to create a finished good',
          duration: 4,
          placement: 'top',
        });
      }
    } else if (uniqueRmSkuIds?.length === 1) {
      notification.open({
        type: 'error',
        message: 'Atleast one raw material is required to create a finished good',
        duration: 4,
        placement: 'top',
      });
    } else {
      for (const key in copyData) {
        if (copyData.hasOwnProperty(key)) {
          const array = copyData[key];
          for (let i = array.length - 1; i >= 0; i--) {
            if (array[i].product_sku_id === productSkuId) {
              array.splice(i, 1);
            }
          }
        }
      }
      setBomLines(copyData);
    }
  };

  // Delete BP line
  const handleDeleteByProduct = (productSkuId, bPKey) => {
    const copyData = JSON.parse(JSON.stringify(bomByProducts));
    const flatData = Object.values(copyData).flat();
    if (finishedGoods?.length === 1) {
      if (flatData?.length > 0) {
        const copyBomLineArray = flatData.filter((item) => item !== null).filter((item) => item.key !== bPKey);
        const copyData1 = fgLinesMapping(copyBomLineArray);
        setBomByProducts(copyData1);
      }
    } else {
      for (const key in copyData) {
        if (copyData.hasOwnProperty(key)) {
          const array = copyData[key];
          for (let i = array.length - 1; i >= 0; i--) {
            if (array[i].product_sku_id === productSkuId) {
              array.splice(i, 1);
            }
          }
        }
      }
      setBomByProducts(copyData);
    }
  };

  // Delete OC line
  const handleDeleteOtherCharges = (key) => {
    const extraChargesLineArray = Object.values(extraCharges).flat();
    if (extraChargesLineArray?.length > 0) {
      const copyextraChargesLineArray = extraChargesLineArray.filter((item) => item.key !== key);
      const copyData = fgLinesMapping(copyextraChargesLineArray);
      setExtraCharges(copyData);
    }
  };

  const handleProductChangeRawMaterial = (tenantSku, key) => {
    const fgTenantProductId = finishedGoods?.[0]?.tenant_product_info?.tenant_product_id;
    const copyData = JSON.parse(JSON.stringify(bomLines[fgTenantProductId]));
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i].key === key) {
        const copyDataItem = copyData[i];
        copyDataItem.asset1 = tenantSku?.product_info?.assets?.[0]?.url || '';
        copyDataItem.tenant_product_info = {
          product_sku_id: tenantSku?.product_sku_id,
          tenant_product_id: tenantSku?.tenant_product_id,
          tenant_id: tenantSku?.tenant_id,
          is_active: tenantSku?.is_active,
          alias_name: tenantSku?.alias_name,
          cost_price: tenantSku?.cost_price,
          uom_info: tenantSku?.uom_info,
          internal_sku_code: tenantSku?.product_info?.internal_sku_code,
          ref_product_code: tenantSku?.product_info?.ref_product_code,
          product_batches: getBatches(1, tenantSku?.product_batches, false, copyDataItem?.good_stock_only),
        };
        copyDataItem.product_info = tenantSku?.product_info;
        copyDataItem.product_category_info = tenantSku?.product_category_info;
        copyDataItem.tenantProductId = tenantSku?.tenant_product_id;
        copyDataItem.currentStock = tenantSku?.available_qty || 0;
        copyDataItem.estimatedUsage = 1;
        copyDataItem.uomId = tenantSku?.product_info?.uom_id || 0;
        copyDataItem.uomInfo = tenantSku?.product_info?.uom_info;
        copyDataItem.uom_info = tenantSku?.product_info?.uom_info;
        copyDataItem.uomGroup = tenantSku?.group_id;
        copyDataItem.costPerUnit = tenantSku?.cost_price;
        copyDataItem.productSkuName = tenantSku?.product_info.product_sku_name;
        copyDataItem.product_sku_name = tenantSku?.product_info.product_sku_name;
        copyDataItem.product_sku_id = tenantSku?.product_info.product_sku_id;
        copyDataItem.tenantProductId = tenantSku?.tenant_product_id;
        copyDataItem.fg_tenant_product_id = fgTenantProductId;
        copyDataItem.wastage_quantity = 0;
        copyDataItem.product_batches = getBatches(1, tenantSku?.product_batches, false, allStock);
        copyDataItem.manufacturingDateFormat = tenantSku?.product_info?.manufacturing_date_format;
        copyDataItem.expiryDateFormat = tenantSku?.product_info?.expiry_date_format;
        copyData[i] = copyDataItem;
      }
    }
    setBomLines({
      ...bomLines,
      [fgTenantProductId]: copyData,
    });
  };

  const handleProductChangeValueRawMaterial = (value, key) => {
    const copyData = JSON.parse(JSON.stringify(bomLines));
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i].key === key) {
        const copyDataItem = copyData[i];
        copyDataItem.product_sku_name = value;
        copyData[i] = copyDataItem;
      }
    }

    setBomLines(copyData);
  };

  const handleProductChangeByProduct = (tenantSku, key) => {
    const fgTenantProductId = finishedGoods?.[0]?.tenant_product_info?.tenant_product_id;
    const copyData = JSON.parse(JSON.stringify(bomByProducts[fgTenantProductId]));
    const batchCustomFields = CustomFieldHelpers.getCfStructure(cfV2DocManufacturingOrder?.data?.batch_custom_fields, true);
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i].key === key) {
        const copyDataItem = copyData[i];
        const newBatch = {
          tenant_product_id: tenantSku?.tenant_product_id,
          expiry_date: tenantSku?.product_info?.expiry_days > 0 ? (dayjs().endOf('day').add(tenantSku?.product_info?.expiry_days, 'day')) : dayjs(INFINITE_EXPIRY_DATE, 'YYYY-MM-DD').endOf('day'),
          lot_number: '',
          custom_batch_number: `${tenantSku?.product_info?.internal_sku_code}/${dayjs().format('DDMMYY')}/${tenantSku?.product_info?.product_batch_counter}`,
          cost_price: 0,
          mrp: 0,
          selling_price: 0,
          uom_id: tenantSku?.uom_info?.uom_id,
          manual_entry: true,
          inventory_location_id: null,
          inventory_location_path: null,
          is_rejected_batch: false,
          custom_fields: batchCustomFields,
        };
        copyDataItem.asset1 = tenantSku?.product_info?.assets?.[0]?.url || '';
        copyDataItem.selectedBatch = newBatch;
        copyDataItem.tenant_product_info = {
          product_sku_id: tenantSku?.product_sku_id,
          tenant_product_id: tenantSku?.tenant_product_id,
          tenant_id: tenantSku?.tenant_id,
          is_active: tenantSku?.is_active,
          alias_name: tenantSku?.alias_name,
          cost_price: tenantSku?.cost_price,
          uom_info: tenantSku?.uom_info,
          internal_sku_code: tenantSku?.internal_sku_code,
          ref_product_code: tenantSku?.ref_product_code,
        };
        copyDataItem.product_info = tenantSku?.product_info;
        copyDataItem.tenantProductId = tenantSku?.tenant_product_id;
        copyDataItem.product_category_info = tenantSku?.product_category_info;
        copyDataItem.currentStock = tenantSku?.available_qty || 0;
        copyDataItem.estimatedUsage = '';
        copyDataItem.uomId = tenantSku?.product_info?.uom_id || 0;
        copyDataItem.uomInfo = tenantSku?.product_info?.uom_info;
        copyDataItem.uomGroup = tenantSku?.group_id;
        copyDataItem.costPerUnit = tenantSku?.cost_price;
        copyDataItem.productSkuName = tenantSku?.product_info.product_sku_name;
        copyDataItem.product_sku_name = `${tenantSku?.product_info.product_sku_name}`;
        copyDataItem.product_sku_id = tenantSku?.product_sku_id;
        copyDataItem.tenantProductId = tenantSku?.tenant_product_id;
        copyDataItem.fg_tenant_product_id = fgTenantProductId;
        copyDataItem.bom_id = finishedGoods?.[0]?.bom_id;
        copyDataItem.manufacturingDateFormat = tenantSku?.product_info?.manufacturing_date_format;
        copyDataItem.expiryDateFormat = tenantSku?.product_info?.expiry_date_format;
        copyData[i] = copyDataItem;
      }
    }
    setBomByProducts({
      ...bomByProducts,
      [fgTenantProductId]: copyData,
    });
  };

  const handleProductChangeValueByProduct = (value, key) => {
    if (bomByProducts?.length) {
      const copyData = JSON.parse(JSON.stringify(bomByProducts));
      for (let i = 0; i < copyData.length; i++) {
        if (copyData[i].key === key) {
          const copyDataItem = copyData[i];
          copyDataItem.product_sku_name = value;
          copyData[i] = copyDataItem;
        }
      }

      setBomByProducts(copyData);
    }
  };

  const handleOtherChargesChange = (fgTenantProductId, chargeName, chargeValue, key) => {
    // Update OC line estimated charges
    const extraChargesCopy = JSON.parse(JSON.stringify(extraCharges));
    const extraChargesLines = extraChargesCopy[fgTenantProductId];
    for (let i = 0; i < extraChargesLines?.length; i++) {
      if (extraChargesLines[i]?.key === key) {
        if (chargeName) {
          extraChargesLines[i].charge_name = chargeName == 'empty' ? '' : chargeName || '';
        }
        if (chargeValue) {
          extraChargesLines[i].charge_amount = chargeValue == 'empty' ? '' : chargeValue;
        }
      }
    }
    extraChargesCopy[fgTenantProductId] = extraChargesLines;

    setExtraCharges(extraChargesCopy);
  };

  const handleProductChangeValueOtherCharge = (value, key) => {
    const copyData = JSON.parse(JSON.stringify(bomByProducts));
    for (let i = 0; i < copyData.length; i++) {
      if (copyData[i].key === key) {
        const copyDataItem = copyData[i];
        copyDataItem.product_sku_name = value;
        copyData[i] = copyDataItem;
      }
    }

    setBomByProducts(copyData);
  };

  function getMoCharges(lines) {
    const skuLines = {};
    const tenantSkuIdList = Object.keys(lines);
    for (let i = 0; i < tenantSkuIdList?.length; i++) {
      const tenantSkuId = tenantSkuIdList[i];
      if (lines[tenantSkuId]?.length) {
        const allSkuLines = [...lines[tenantSkuId]];
        for (let j = 0; j < allSkuLines?.length; j++) {
          if (skuLines.hasOwnProperty(allSkuLines[j]?.charge_name)) {
            if (finishedGoods?.length !== 1) {
              skuLines[allSkuLines[j]?.charge_name].charge_amount += (allSkuLines[j]?.costPerUnit * allSkuLines[j]?.fgQuantity);
            }
          } else if (finishedGoods?.length !== 1) {
            skuLines[allSkuLines[j]?.charge_name] = {
              ...allSkuLines[j],
              charge_amount: allSkuLines[j]?.costPerUnit * allSkuLines[j]?.fgQuantity,
            };
          } else {
            skuLines[allSkuLines[j]?.charge_name] = {
              ...allSkuLines[j],
            };
          }
        }
      }
    }
    return Object.values(skuLines);
  }

  function handleToggleRMBatch(record, productBatchesRow, fgTenantProductId) {
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const productBomLines = bomLinesCopy[fgTenantProductId];

    const handleToggleBatch = (record, productBatchesRow) => {
      for (let i = 0; i < productBomLines?.length; i++) {
        if (productBomLines[i]?.key === productBatchesRow?.key) {
          const rowBatches = productBomLines[i]?.product_batches;
          for (let j = 0; j < rowBatches?.length; j++) {
            if (rowBatches[j]?.batch_id === record?.batch_id) {
              rowBatches[j].batch_in_use = !rowBatches[j]?.batch_in_use;
              productBomLines[i].wastage = 0;
              productBomLines[i].estimatedUsage -= rowBatches[j].consumed_qty;
              rowBatches[j].consumed_qty = 0;
            }
          }
          break;
        }
      }
    };
    handleToggleBatch(record, productBatchesRow);
    bomLinesCopy[fgTenantProductId] = productBomLines;
    setBomLines(bomLinesCopy);
  }

  const handleCreateManualEntry = (createNew) => {
    setFormSubmitted(true);
    if (selectedTenant && isDataValid()?.isValid) {
      const temoMoFinishedGoods = finishedGoods.map((item) => ({
        bom_id: item?.bom_info?.bom_id,
        quantity: item?.quantity,
        fg_tenant_product_id: item?.bom_info?.tenant_product_info?.tenant_product_id,
        product_sku_id: item?.bom_info?.tenant_product_info?.product_sku_id || item?.tenant_product_id,
        tenant_product_id: item?.bom_info?.tenant_product_info?.tenant_product_id || item?.tenant_product_id,
        uom_id: item?.bom_info?.tenant_product_info?.uom_info?.uom_id || item?.uom_info?.uom_id || item?.bom_info?.product_info?.uom_info?.uom_id,
        uom_info: item?.bom_info?.tenant_product_info?.uom_info || item?.uom_info || item?.bom_info?.product_info?.uom_info,
        product_batches: [{
          ...item?.selectedBatch,
          quantity: item?.quantity,
          uom_id: item?.bom_info?.tenant_product_info?.uom_info?.uom_id || item?.bom_info?.product_info?.uom_info?.uom_id,
          tenant_department_id: fgTenantDepartmentId,
          manufacturing_date: item?.selectedBatch?.manufacturing_date ? FormHelpers.dateFormatter(item?.selectedBatch?.manufacturing_date, item?.manufacturingDateFormat) : null,
          expiry_date: item?.selectedBatch?.expiry_date ? FormHelpers.dateFormatter(item?.selectedBatch?.expiry_date, item?.expiryDateFormat) : null,
          custom_fields: CustomFieldHelpers.postCfStructure(item?.selectedBatch?.custom_fields),
        }],
        remarks: '',
      }));
      const tempRMLines = Object.keys(bomLines).map((item) => bomLines[item]?.map((innerItem) => ({
        tenant_product_id: innerItem?.tenantProductId || innerItem?.tenant_product_id,
        wastage_quantity: Number(innerItem?.wastage_quantity || 0),
        quantity: innerItem?.estimatedUsage,
        bom_line_id: innerItem?.bomLineId,
        bom_id: innerItem?.bom_id,
        uom_id: innerItem?.uomId,
        uom_info: innerItem?.uomInfo,
        product_sku_id: innerItem?.product_sku_id,
        fg_tenant_product_id: innerItem?.fg_tenant_product_id,
        product_batches: innerItem?.product_batches?.map((item) => ({
          ...item,
          uom_id: innerItem?.uomInfo?.uom_id,
          quantity: -item?.consumed_qty,
          manufacturing_date: item?.manufacturing_date ? FormHelpers.dateFormatter(item?.manufacturing_date, innerItem?.manufacturingDateFormat) : null,
          expiry_date: item?.expiry_date ? FormHelpers.dateFormatter(item?.expiry_date, innerItem?.expiryDateFormat) : null,
        })),
        child_bom_id: innerItem?.child_bom_id,
        parent_bom_id: innerItem?.parent_bom_id,
        parent_quantity_per_unit: innerItem?.parentQuantityPerUnit,
      })))?.flat(1).filter((innerItem) => innerItem?.tenant_product_id);

      const tempByProductLines = Object.keys(bomByProducts).map((item) => bomByProducts[item]?.map((innerItem) => ({
        product_sku_id: innerItem?.product_info?.product_sku_id,
        tenant_product_id: innerItem?.tenantProductId,
        quantity: innerItem?.estimatedUsage,
        bom_by_product_id: innerItem?.bomByProductId,
        bom_id: innerItem?.bom_id,
        uom_id: innerItem?.uomId || innerItem?.uom_id,
        uom_info: innerItem?.uomInfo,
        fg_tenant_product_id: innerItem?.fg_tenant_product_id,
        product_batches: [
          {
            ...innerItem?.selectedBatch,
            uom_id: innerItem?.uomInfo?.uom_id,
            quantity: innerItem?.estimatedUsage,
            tenant_department_id: bpTenantDepartmentId,
            manufacturing_date: innerItem?.selectedBatch?.manufacturing_date ? FormHelpers.dateFormatter(innerItem?.selectedBatch?.manufacturing_date, innerItem?.manufacturingDateFormat) : null,
            expiry_date: innerItem?.selectedBatch?.expiry_date ? FormHelpers.dateFormatter(innerItem?.selectedBatch?.expiry_date, innerItem?.expiryDateFormat) : null,
            custom_fields: CustomFieldHelpers.postCfStructure(innerItem?.selectedBatch?.custom_fields),
          }],
      })))?.flat(1).filter((innerItem) => innerItem?.tenant_product_id);

      const tempExtraCharges = Object.keys(extraCharges).map((item) => extraCharges[item]
        ?.map((innerItem) => ({
          charge_name: innerItem.charge_name,
          charge_amount: finishedGoods?.length !== 1 ? innerItem?.costPerUnit * innerItem?.fgQuantity : innerItem?.charge_amount,
          bom_id: innerItem?.bom_id,
          fg_tenant_product_id: innerItem?.fg_tenant_product_id,
        })))?.flat(1).filter((innerItem) => innerItem?.charge_name);
      const payload = {
        org_id: user?.tenant_info?.org_id,
        tenant_department_id: tenantDepartmentId,
        fg_tenant_department_id: fgTenantDepartmentId,
        rm_tenant_department_id: rmTenantDepartmentId,
        bp_tenant_department_id: bpTenantDepartmentId,
        enable_wastage_tracking: trackWastage,
        tenant_id: selectedTenant,
        status: 'ISSUED',
        raw_materials: tempRMLines,
        finished_goods: temoMoFinishedGoods,
        by_products: tempByProductLines,
        extra_charges: tempExtraCharges,
        remarks,
        attachments: fileList?.map((attachment) => ({
          url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
        })) || [],
        production_entry_date: dayjs(scheduledDate).format('YYYY-MM-DD'),
        production_entry_number: initialMeNumber?.toLowerCase()?.trim() === meNumber?.toLowerCase()?.trim() ? null : meNumber,
        seq_id: docSeqId || null,
      };
      createManualProductionEntry(payload, (mpe) => {
        setActionType('');
        if (createNew) {
          setCurrentTab('/finished-goods');
          setFinishedGoods([{
            key: uuidv4(), asset1: '', product_name: '', quantity: '', unitPrice: '', lot: 0, taxId: '', discount: 0,
          }]);
          setBomLines({});
          setBomByProducts({});
          setExtraCharges({});
          setFileList([]);
          setFormSubmitted(false);
          setActionType('');
          setIsFgBatchValid(false);
          setRemarks('');
        } else {
          history.push('/production/manual-entry');
        }
      });
    } else {
      notification.open({
        type: 'error',
        message: 'There are some problems in the data provided by you.',
        duration: 4,
        placement: 'top',
      });
    }
  };

  useEffect(() => {
    if (finishedGoods?.[0]?.tenant_product_info) {
      handleFgQuantityChange(finishedGoods[0]?.tenant_product_info?.tenant_product_id, finishedGoods[0]?.quantity, finishedGoods[0]?.key);
    }
  }, [trackWastage]);

  function updateRmBatchValue(key, value, label, batchId, fgTenantProductId) {
    const bomLinesCopy = JSON.parse(JSON.stringify(bomLines));
    const productBomLines = bomLinesCopy[fgTenantProductId];

    const updatedProductBomLines = productBomLines.map((obj) => {
      if (obj.key === key) {
        let productBatches = obj?.product_batches;
        productBatches = productBatches?.map((productBatch) => {
          if (productBatch.batch_id === batchId) {
            return {
              ...productBatch,
              [`${label}`]: value,
            };
          }
          return productBatch;
        });

        // const qty = productBatches.reduce((sum, batch) => sum + (Number(batch.consumed_qty) || 0), 0);
        const qty = productBatches.reduce(
          (sum, batch) => sum.plus(new Decimal(batch.consumed_qty || 0)),
          new Decimal(0)
        );
        return {
          ...obj,
          product_batches: productBatches,
          estimatedUsage: qty,
        };
      }
      return obj;
    });

    bomLinesCopy[fgTenantProductId] = updatedProductBomLines;
    setBomLines(bomLinesCopy);  } 

  const renderInputs = (item) => {
    if(item?.type === 'docSequence') {
      return (  
        <DocumentNumberSeqInput
          valueFromProps={meNumber}
          setInitialDocSeqNumber={(value) => setInitialMeNumber(value)}
          entityName="MANUAL_PRODUCTION_ENTRY"
          docSeqId={docSeqId}
          tenantId={selectedTenant}
          onChangeFromProps={(event, newValue, seqId) => {
            setMeNumber( newValue ? (newValue || '') : (event?.target?.value || ''));
            setDocSeqId(seqId);
          }}
          docTitle={item?.title}
          formSubmitted={formSubmitted}
          rowClassName="ant-col-md-24"
          inputRowClassName="manual-entry__input-row"
          labelClassName="manual-entry__input-row__label"
          containerClassName="orgInputContainer "
          inputWrapperClassName="manual-entry__input-row__input"
          inputClassName="orgFormInput input"
          selectorStyle={{
            opacity: 0,
            backgroundColor: 'transparent',
            zIndex: 1,
            top: '-22px',
            width: '20%',
            right: '1px',
          }}
          seqClassName="manual-entry__doc-seq-number"
          callback={formSubmitted}
        />
      )
    }

    if (item?.type === 'tenant') {
      return (
        <div className="ant-col-md-24">
          <div className="manual-entry__input-row">
            <H3Text required text={item?.title} className="manual-entry__input-row__label" />
            <div className="manual-entry__input-row__input">  
              <TenantSelector
                hideTitle
                selectedTenant={item?.value}
                showSearch
                onChange={(value) => handleTenantChange(value)}
                showAll={false}
                inputClassName="input"
                containerClassName="orgInputContainer"
                noDropdownAlign
                customStyle={{
                  borderRadius: '4px',
                  height: '28px',
                  padding: '0px',
                }}
                placeholder="Select Business Unit"
                includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.CREATE)}
              />
            </div>
          </div>
        </div>  
      );
    }

    if (item?.type === 'remarks') {
      return (
        <div className="ant-col-md-24">
          <div className="manual-entry__input-row">
            <label className="manual-entry__input-row__label">
              Internal Remarks
            </label>
            <textarea
              className="manual-entry__input-row__input "
              style={{
                border: '1px solid rgba(68, 130, 218, 0.2)',
                fontSize: '12px',
              }}
              rows="3"
              cols="50"
              onChange={(event) => setRemarks(event.target.value)}
              value={remarks}
              disabled={createManualProductionEntryLoading}
            />
          </div>
        </div>
      );
    }

    if (item?.type === 'department') {
      return (
        <Fragment>
          {(item?.departmentType !== 'PRODUCTION' ? showAdvancedSettings : true) && (
            <div className="ant-col-md-24">
              <div className="manual-entry__input-row">
                <H3Text required text={item?.title} className="manual-entry__input-row__label" />
                <div className="manual-entry__input-row__input">
                  <SelectDepartment
                    hideTitle
                    tenantId={selectedTenant}
                    selectedDepartment={item?.value}
                    noDropdownAlign
                    onChange={(value) => handleDepartmentChange(value?.tenant_department_id, item?.departmentType)}
                    tenentLevelDepartment
                    emptyNotAllowed
                    customStyle={{
                      borderRadius: '4px',
                      height: '28px',
                      padding: '0px',
                    }}
                    labelClassName="manual-entry__input-row__label"
                    inputClassName="input"
                  />
                </div>
              </div>
            </div>
          )}
        </Fragment>
      );
    }
    if (item?.type === 'date') {
      return (
        <div className="ant-col-md-24">
          <div className="manual-entry__input-row orgInputContainer">
            <H3Text required text={item?.title} className="manual-entry__input-row__label" />
            <div className="manual-entry__input-row__input">
              <DatePicker
                value={item?.value}
                onChange={(value) => {
                  setScheduledDate(value);
                }}
                style={{
                  borderRadius: '2px',
                  height: '28px',
                  padding: '1px 3px',
                  width: '100%',
                  background: 'white',
                  border: '1px solid rgba(68, 130, 218, 0.2)'
                }}
                format="DD-MMM-YYYY"
              />
              {(formSubmitted && !scheduledDate) && (
                <div className="input-error">
                  *Please enter scheduled date
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (item?.type === 'button') {
      return (
        <div className="ant-col-md-24">
          <div className="manual-entry__input-row">
            <label className="manual-entry__input-row__label" />
            <div className="manual-entry__input-row__input">
              <div className="manual-entry__input-row-more" onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}>
                {item?.title}
                &nbsp;
                <FontAwesomeIcon icon={showAdvancedSettings ? faChevronDown : faChevronRight} />
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (item?.type === 'checkbox') {
      return (
        showAdvancedSettings && (
          <div className="ant-col-md-24">
            <div className="manual-entry__input-row" style={{ marginBottom: '10px' }}>
              <label className="manual-entry__input-row__label" />
              <Checkbox
                checked={item?.value}
                onChange={() => {
                  setTrackWastage(!trackWastage);
                }}
                className="manual-entry__input-row__input"
              >
                {item?.title}
              </Checkbox>
            </div>
          </div>
        )
      );
    }
  };

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  return (
    <Fragment>
      <div className="manual-entry__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-12">
            <div className="ant-row">
              {inputArray?.map((item) => (
                renderInputs(item)
              ))}
            </div>
          </div>
          <div className="ant-col-md-12">

            {
              formSubmitted && !isDataValid()?.isValid ? (
                <div className="mpe-form-errors">
                  <Alert
                    type="error"
                    showIcon
                    message="You have below errors in the document"
                    description={(
                      <ul>
                        {!meNumber ? (
                          <li>
                            Manual Entry Number missing
                          </li>
                        ) : ''}
                        {isDataValid()?.rmErrorCount ? (
                          <li>
                            {`You have shortage of ${isDataValid()?.rmErrorCount} raw materials`}
                          </li>
                        ) : ''}
                        {!isDataValid()?.isFgBatchValid ? (
                          <li>
                            Finished goods batch has missing information
                          </li>
                        ) : ''}
                        {!isDataValid()?.isBpBatchValid ? (
                          <li>
                            By product batch has missing information
                          </li>
                        ) : ''}
                        {!isDataValid()?.isFgQuantityValid ? (
                          <li>
                            Finished Goods quantity is not valid
                          </li>
                        ) : ''}
                        {!isDataValid()?.isBpQuantityValid ? (
                          <li>
                            By product quantity is not valid
                          </li>
                        ) : ''}
                      </ul>
                    )}
                  />
                </div>
              ) : ''
            }
          </div>

          <div className="ant-col-md-24">
            <div>
              <div style={{ width: '100%', marginTop: '20px' }}>
                <Tabs
                  activeKey={currentTab}
                  onChange={(key) => {
                    setCurrentTab(key);
                  }}
                  mode="horizontal"
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        Finished Goods &nbsp;
                        <Badge count={finishedGoods?.filter((item) => item?.bom_id)?.length} />
                      </span>
                    )}
                    key="/finished-goods"
                  />
                  <TabPane
                    tab={(
                      <span>
                        Raw Materials &nbsp;
                        <Badge count={getMoComponents(bomLines)?.filter((item) => !item?.fg_product)?.length} />
                      </span>
                    )}
                    key="/raw-materials"
                    disabled={!finishedGoods?.filter((item) => item?.bom_id)?.length}
                  />
                  <TabPane
                    tab={(
                      <span>
                        By Products &nbsp;
                        <Badge count={getMoComponents(bomByProducts)?.filter((item) => !item?.fg_product)?.length} />
                      </span>
                    )}
                    key="/by-products"
                    disabled={!finishedGoods?.filter((item) => item?.bom_id)?.length}
                  />
                  <TabPane
                    tab={(
                      <span>
                        Additional Charges &nbsp;
                        <Badge count={getMoCharges(extraCharges)?.filter((item) => item?.charge_amount > 0)?.length} />
                      </span>
                    )}
                    key="/additional-charges"
                    disabled={!finishedGoods?.filter((item) => item?.bom_id)?.length}
                  />
                </Tabs>
              </div>

              {currentTab === '/finished-goods' && (
                <div className="manual-entry__lines-wrapper">
                  <FinishedGood
                    handleDelete={handleDeleteFinishedGood}
                    handleProductChange={onSelectBom}
                    handleQuantityChange={handleFgQuantityChange}
                    handleChangeRemarks={handleChangeRemarks}
                    data={finishedGoods}
                    getReadyToProduce={(fgTpId) => getReadyToProduce(fgTpId)}
                    updateData={(updatedData) => setFinishedGoods(updatedData)}
                    formSubmitted={formSubmitted}
                    loading={getMOByIdLoading || createManualProductionEntryLoading || updateMOLoading || getBOMByIdLoading}
                    excludedProducts={getExcludedProduct()}
                    tenantDepartmentId={fgTenantDepartmentId}
                    rmTenantDepartmentId={rmTenantDepartmentId}
                    selectedTenant={selectedTenant}
                    includeInActiveBOM
                    getFgCosting={(fgId, qty) => getFgCosting(fgId, qty)}
                    setIsBatchValid={(value) => setIsFgBatchValid(value)}
                  />
                </div>
              )}

              {currentTab === '/raw-materials' && (finishedGoods?.length !== 1 ? getMoComponents(bomLines)?.length > 0 : (finishedGoods?.length === 1 && finishedGoods?.[0]?.bom_id)) && (
                <div className="manual-entry__lines-wrapper">
                  <RawMaterial
                    handleDelete={handleDeleteRawMaterial}
                    handleProductChange={handleProductChangeRawMaterial}
                    handleProductChangeValue={handleProductChangeValueRawMaterial}
                    handleQuantityChange={handleRmQuantityChange}
                    handleToggleBatch={handleToggleRMBatch}
                    data={getMoComponents(bomLines)}
                    allStock={allStock}
                    updateAllStock={(value, fgTenantProductId, key) => {
                      updateAllStockValue(value, fgTenantProductId, key);
                    }}
                    addNewRow={() => addNewRowRawMaterial()}
                    updateData={(updatedData) => {
                      setBomLines(updatedData);
                    }}
                    formSubmitted={formSubmitted}
                    loading={getMOByIdLoading || createManualProductionEntryLoading || updateMOLoading || getBOMByIdLoading}
                    excludedProducts={getExcludedProduct()}
                    tenantDepartmentId={rmTenantDepartmentId}
                    multipleFG={finishedGoods?.length !== 1}
                    trackWastage={trackWastage}
                    updateRmBatchConsumptionMethod={(fgId, method, key) => updateRmBatchConsumptionMethod(fgId, method, key)}
                    updateBatchValue={(key, value, label, batchId, fgId) => updateRmBatchValue(key, value, label, batchId, fgId)}
                  />
                  {(finishedGoods?.length === 1 && finishedGoods?.[0]?.bom_id)
                    && (
                      <div className="new-row-button" onClick={() => ((!createManualProductionEntryLoading && Object.values(bomLines).flat()?.every((item) => item?.productSkuName)) && addNewRowRawMaterial())}>
                        <span className="new-row-button__icon"><PlusCircleFilled /></span>
                        <div>New Item</div>
                      </div>
                    )}
                </div>
              )}

              {currentTab === '/by-products' && (
                <div style={{ fontWeight: '600px', width: '70%' }}>
                  <ByProduct
                    handleDelete={handleDeleteByProduct}
                    handleProductChange={handleProductChangeByProduct}
                    handleProductChangeValue={handleProductChangeValueByProduct}
                    handleQuantityChange={handleBpQuantityChange}
                    data={getMoComponents(bomByProducts)}
                    addNewRowByProduct={() => addNewRowByProduct()}
                    updateData={(updatedData) => setBomByProducts(updatedData)}
                    formSubmitted={formSubmitted}
                    loading={getMOByIdLoading || createManualProductionEntryLoading || updateMOLoading || getBOMByIdLoading}
                    excludedProducts={getExcludedProduct()}
                    tenantDepartmentId={bpTenantDepartmentId}
                    multipleFG={finishedGoods?.length !== 1}
                    setIsBatchValid={(value) => setIsBpBatchValid(value)}
                  />
                  {(finishedGoods?.length === 1 && finishedGoods?.[0]?.bom_id) && (
                    <div className="new-row-button" onClick={() => ((!createManualProductionEntryLoading && !updateMOLoading && (Object.values(bomByProducts).flat()?.length > 1 ? Object.values(bomByProducts).flat()?.every((item) => item?.productSkuName) : true)) && addNewRowByProduct())}>
                      <span className="new-row-button__icon"><PlusCircleFilled /></span>
                      <div>New Item</div>
                    </div>
                  )}
                </div>
              )}

              {currentTab === '/additional-charges' && (
                <div className="manual-entry__lines-wrapper" style={{ width: '60%' }}>
                  <OtherCharge
                    handleDelete={handleDeleteOtherCharges}
                    handleChargeChange={handleOtherChargesChange}
                    data={finishedGoods?.length !== 1 ? getMoCharges(extraCharges)?.filter((item) => item?.charge_amount > 0) : getMoCharges(extraCharges)}
                    updateData={(updatedData) => setExtraCharges(updatedData)}
                    formSubmitted={formSubmitted}
                    loading={getMOByIdLoading || createManualProductionEntryLoading || updateMOLoading || getBOMByIdLoading}
                    handleProductChangeValue={handleProductChangeValueOtherCharge}
                    multipleFG={finishedGoods?.length !== 1}
                  />
                </div>
              )}

            </div>

            <div className="manual-entry__lines-wrapper" style={{ marginTop: '20px' }}>
              <div className="text-area__po-container">
                <label className="orgFormLabel">
                  Attachment(s)
                </label>
                <Upload
                  action={Constants.UPLOAD_FILE}
                  listType="picture-card"
                  fileList={fileList}
                  disabled={createManualProductionEntryLoading || updateMOLoading}
                  multiple
                  onChange={(fileListData) => {
                    setFileList(fileListData?.fileList?.map((item) => ({
                      ...item,
                      url: item?.response?.response?.location || item?.url,
                    })));
                  }}
                >
                  {fileList?.length >= 20 ? null : uploadButton}
                </Upload>

              </div>
            </div>
          </div>
        </div>

        <div className="manual-entry__footer">
          <H3Button
            text="Save and Create New"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="manual-entry__footer-draft"
            onClick={() => {
              setActionType('SAVE_AND_CREATE');
              if (!createManualProductionEntryLoading) {
                handleCreateManualEntry(true);
              }
            }}
            isLoading={(createManualProductionEntryLoading || updateMOLoading) && actionType === 'SAVE_AND_CREATE'}
            disabled={(createManualProductionEntryLoading || updateMOLoading) && actionType === 'SAVE_AND_CREATE'}
          />
          <H3Button
            text="Save and Issue"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            customClass="manual-entry__footer-submit"
            onClick={() => {
              setActionType('SAVE_AND_ISSUE');
              if (!createManualProductionEntryLoading) {
                handleCreateManualEntry(false);
              }
            }}
            isLoading={(createManualProductionEntryLoading || updateMOLoading) && actionType === 'SAVE_AND_ISSUE'}
            disabled={(createManualProductionEntryLoading || updateMOLoading) && actionType === 'SAVE_AND_ISSUE'}
          />
          {isDataMaskingPolicyEnable && (isHideCostPrice || isHideSellingPrice) && <RestrictedAccessMessage message={`You don't have access to view or edit${isHideCostPrice ? ' unit price/cost price' : ''}${isHideCostPrice && isHideSellingPrice ? ' and' : ''} ${isHideSellingPrice ? 'selling price' : ''}`} />}
        </div>

      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, MOReducers, CFV2Reducers, BOMReducers, InventoryLocationReducers, ManualProductionEntryReducers,
}) => ({
  user: UserReducers.user,
  org: UserReducers.org,
  createManualProductionEntryLoading: ManualProductionEntryReducers.createManualProductionEntryLoading,
  getMOByIdLoading: MOReducers.getMOByIdLoading,
  selectedMO: MOReducers.selectedMO,
  getDocCFV2Loading: CFV2Reducers.getDocCFV2Loading,
  cfV2DocManufacturingOrder: CFV2Reducers.cfV2DocManufacturingOrder,
  getBOMByIdLoading: BOMReducers.getBOMByIdLoading,
  inventoryLocations: InventoryLocationReducers.inventoryLocations,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  createManualProductionEntry: (payload, callback) => dispatch(ManualProductionEntryActions.createManualProductionEntry(payload, callback)),
  getBOMById: (orgId, tenantId, bomId, tenantDepartmentId, bomViewType, callback) => dispatch(BOMActions.getBOMById(orgId, tenantId, bomId, tenantDepartmentId, bomViewType, callback)),
  getDocCFV2Success: (customFields) => dispatch(CFV2Actions.getDocCFV2Success(customFields)),
  getDocCFV2: (payload, callback) => dispatch(CFV2Actions.getDocCFV2(payload, callback)),
  getBOMByIdSuccess: (selectedBOM) => dispatch(BOMActions.getBOMByIdSuccess(selectedBOM)),
  getInventoryLocations: (tenantDepartmentId, inventoryLocationPath, parentLocationId, isStorable, tenantId, departmentId, callback) => dispatch(inventoryLocationActions.getInventoryLocations(tenantDepartmentId, inventoryLocationPath, parentLocationId, isStorable, tenantId, departmentId, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ManualProductionEntryForm));
